/**
 * DOM wiring for the customer-facing "missing details" request page.
 *
 * Only ever runs when the URL carries ?missing-details-token= - sections/image-upload.liquid
 * calls init() for that case only, and the markup this file drives (snippets/missing-details-page.liquid)
 * stays `hidden` otherwise.
 *
 * Business logic (which multipart fields to send, whether the form is complete enough to
 * submit) lives in assets/missing-details-page.js and is unit-tested there - this file only
 * reads the DOM, calls the API, and reflects the response back into the DOM.
 *
 * Photo capture: the pre-purchase upload flow's drag-and-drop/resize code
 * (sections/image-upload.liquid, ~line 1900+) is a plain <input type="file"> wired straight
 * into that flow's own globals (imageInput, canvas, localStorage keys, its own submit
 * endpoint) with no separate module to call into - reusing it here would mean either
 * duplicating that coupling or refactoring a 3900-line file well outside this task's scope.
 * This page therefore uses its own plain <input type="file">, reusing only the already-
 * modular assets/image-upload-safety.js for client-side type/size validation - a plain file
 * input is the disclosed fallback the task brief allows for exactly this situation.
 */
(function () {
  'use strict';

  var M = window.MissingDetailsPage || null;

  // This page uploads the raw file with no client-side downscaling (unlike the pre-purchase
  // flow, which shrinks to 2000px/q0.7 before sending - see the file-level comment above).
  // ImageUploadSafety.validateImageFile()'s default ceiling (MAX_UPLOAD_BYTES, 25MB) is sized
  // for that downscaled flow, so it must be overridden here to match the real server-side
  // multer limit on POST /api/missing-details/:token/submit in
  // TellMyTale-Klaviyo-Integration-API (fileSize: 10 * 1024 * 1024).
  var MAX_PHOTO_BYTES = 10 * 1024 * 1024;

  // Same pencil icon as the main personalization page's upload button
  // (sections/image-upload.liquid's <label class="image-upload__button">), copied verbatim
  // so both buttons render identically. Static markup, so innerHTML is fine here.
  var PHOTO_BUTTON_ICON_SVG =
    '<svg width="18" height="18" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M22.8021 6.83521C23.3604 6.25631 23.6692 5.48124 23.6619 4.67701C23.6547 3.87278 23.3319 3.10368 22.7633 2.53492C22.1946 1.96616 21.4256 1.64293 20.6214 1.63551C19.8172 1.62808 19.042 1.93671 18.463 2.49488L3.93416 17.0271C3.68155 17.2778 3.49451 17.5868 3.38997 17.927L1.95198 22.6638C1.91062 22.8021 1.92603 22.9513 1.99446 23.0784C2.0629 23.2055 2.17874 23.3004 2.31697 23.3421C2.41911 23.3723 2.52782 23.3723 2.62996 23.3421L7.36898 21.905C7.70879 21.8012 8.01801 21.6154 8.26912 21.364L22.8021 6.83521Z" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>' +
    '<path d="M16.0811 4.86292L20.4351 9.21692" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path>' +
    '</svg>';

  // Read once: labels come from settings/locale via data attributes on #tmt-md, set by
  // snippets/missing-details-page.liquid. #tmt-md is always rendered (just hidden), and this
  // script is `defer`, so the element exists by the time this IIFE runs. English fallbacks
  // cover the case where the attribute itself is missing for some reason.
  var LABELS = (function () {
    var el = document.getElementById('tmt-md');
    var ds = (el && el.dataset) || {};
    return {
      upload: ds.uploadLabel || 'Upload Photo',
      gender: ds.genderLabel || 'Gender',
      boy: ds.boyLabel || 'Boy',
      girl: ds.girlLabel || 'Girl'
    };
  })();

  // Read lazily, not captured once at load time: this file's own <script> tag comes before
  // image-upload-safety.js's in sections/image-upload.liquid, so a top-level
  // `window.ImageUploadSafety` read here would run before that script has defined it (all
  // three are `defer`, so they execute in document order, before DOMContentLoaded fires) -
  // window.ImageUploadSafety would be captured as undefined forever. Reading it fresh on
  // every file selection - same lazy-read pattern already used at
  // sections/image-upload.liquid:523 - means it's found regardless of that ordering.
  function getSafety() {
    return window.ImageUploadSafety || null;
  }

  var state = {
    apiBase: null,
    token: null,
    items: [],
    values: {}, // itemId -> { [param]: value }
    submitting: false
  };

  function $(id) {
    return document.getElementById(id);
  }

  function init(options) {
    if (!$('tmt-md')) return;
    if (!M) {
      // Without the pure module there is nothing safe to build a payload with - fail
      // closed rather than let the customer fill out a form that can never submit.
      console.error('[missing-details-page] MissingDetailsPage module not loaded; aborting.');
      return;
    }

    state.apiBase = options.apiBase;
    state.token = options.token;

    $('tmt-md').hidden = false;

    bindSubmit();
    load();
  }

  /* ---------------------------------------------------------------- load */

  function load() {
    showStatus('Loading…');

    return fetch(state.apiBase + 'missing-details/' + encodeURIComponent(state.token))
      .then(function (res) {
        if (res.status === 404) {
          showAllDone();
          return null;
        }
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        if (!data) return;

        if (data.allSubmitted || !Array.isArray(data.items) || data.items.length === 0) {
          showAllDone();
          return;
        }

        state.items = data.items;
        state.values = {};
        data.items.forEach(function (item) {
          state.values[item.id] = {};
        });

        showOrderNumber(data.orderNumber);
        renderItems(data.items);
        hideStatus();
        $('tmt-md-actions').hidden = false;
        updateSubmitEnabled();
      })
      .catch(function () {
        showStatus('We couldn’t load your request right now. Please refresh the page, or reply to your order email and we’ll help.');
      });
  }

  function showOrderNumber(orderNumber) {
    var el = $('tmt-md-order');
    if (!orderNumber) {
      el.hidden = true;
      return;
    }
    el.textContent = 'Order ' + orderNumber;
    el.hidden = false;
  }

  function showStatus(text) {
    $('tmt-md-status-text').textContent = text;
    $('tmt-md-status').hidden = false;
    $('tmt-md-items').hidden = true;
    $('tmt-md-actions').hidden = true;
  }

  function hideStatus() {
    $('tmt-md-status').hidden = true;
    $('tmt-md-items').hidden = false;
  }

  function showAllDone() {
    $('tmt-md-items').hidden = true;
    $('tmt-md-actions').hidden = true;
    $('tmt-md-order').hidden = true;
    showStatus('There’s nothing pending for you right now - thanks!');
  }

  function showSuccess() {
    $('tmt-md-items').hidden = true;
    $('tmt-md-actions').hidden = true;
    $('tmt-md-status').hidden = true;
    $('tmt-md-success').hidden = false;
  }

  /* -------------------------------------------------------------- render */

  function renderItems(items) {
    var container = $('tmt-md-items');
    container.innerHTML = '';

    items.forEach(function (item) {
      container.appendChild(buildCard(item));
    });
  }

  function buildCard(item) {
    var card = document.createElement('div');
    card.className = 'tmt-md-card';
    card.id = 'tmt-md-card-' + item.id;

    var title = document.createElement('h2');
    title.className = 'tmt-md-card__title';
    title.textContent = item.productTitle || 'Your book';
    card.appendChild(title);

    var fields = (item && item.missingFields) || [];
    fields.forEach(function (field) {
      var wrap = buildFieldForKey(field, item.id);
      if (wrap) card.appendChild(wrap);
    });

    var error = document.createElement('p');
    error.className = 'tmt-md-card__error';
    error.id = 'tmt-md-card-error-' + item.id;
    error.hidden = true;
    card.appendChild(error);

    return card;
  }

  function buildFieldForKey(field, itemId) {
    if (!M) return null;
    var spec = M.specFor(field.key);
    if (!spec) return null; // an unrecognised key renders nothing, rather than guessing at an input

    if (spec.kind === 'photo') return buildPhotoField(field, itemId, spec.param);
    if (spec.kind === 'text') return buildTextField(field, itemId, spec.param, 'text');
    if (spec.kind === 'age') return buildTextField(field, itemId, spec.param, 'number');
    return buildChoiceField(field, itemId, spec.param);
  }

  // `param` keeps two photos on one two-person card apart; for the single photo it is
  // 'photo', so the ids stay tmt-md-photo-<id> / tmt-md-photo-preview-<id> as before.
  function buildPhotoField(field, itemId, param) {
    var wrap = document.createElement('div');
    wrap.className = 'tmt-md-field';

    var title = document.createElement('p');
    title.className = 'tmt-md-field__title';
    title.textContent = field.label || 'Upload a photo';
    wrap.appendChild(title);

    var inputId = 'tmt-md-' + param + '-' + itemId;

    var label = document.createElement('label');
    label.setAttribute('for', inputId);
    label.className = 'image-upload__button';
    label.innerHTML = PHOTO_BUTTON_ICON_SVG;

    var span = document.createElement('span');
    span.textContent = LABELS.upload;
    label.appendChild(span);
    wrap.appendChild(label);

    var input = document.createElement('input');
    input.type = 'file';
    input.id = inputId;
    input.accept = 'image/jpeg,image/png,image/webp,image/heic,image/heif';
    wrap.appendChild(input);

    var preview = document.createElement('img');
    preview.className = 'tmt-md-photo-preview';
    preview.id = 'tmt-md-' + param + '-preview-' + itemId;
    preview.hidden = true;
    preview.alt = 'Selected photo preview';
    wrap.appendChild(preview);

    input.addEventListener('change', function () {
      var file = input.files && input.files[0];
      var safety = getSafety();
      var check = safety
        ? safety.validateImageFile(file, { maxBytes: MAX_PHOTO_BYTES })
        : { ok: Boolean(file) };

      if (!check.ok) {
        if (check.reason !== 'no_file') showCardError(itemId, check.message);
        input.value = '';
        setValue(itemId, param, null);
        preview.hidden = true;
        updateSubmitEnabled();
        return;
      }

      clearCardError(itemId);
      setValue(itemId, param, file);

      var reader = new FileReader();
      reader.onload = function (e) {
        preview.src = e.target.result;
        preview.hidden = false;
      };
      reader.readAsDataURL(file);

      updateSubmitEnabled();
    });

    return wrap;
  }

  function buildTextField(field, itemId, param, inputType) {
    var isAge = inputType === 'number';
    var wrap = document.createElement('div');
    wrap.className = 'tmt-md-field';

    var inputId = 'tmt-md-' + param + '-' + itemId;

    var label = document.createElement('label');
    label.setAttribute('for', inputId);
    label.textContent = field.label || (isAge ? "Child's age" : "Child's name");
    wrap.appendChild(label);

    var input = document.createElement('input');
    input.type = inputType;
    input.id = inputId;
    if (isAge) {
      input.min = 1;
      input.max = 100;
    } else {
      input.maxLength = 30;
    }
    wrap.appendChild(input);

    input.addEventListener('input', function () {
      clearCardError(itemId);
      setValue(itemId, param, input.value);
      updateSubmitEnabled();
    });

    return wrap;
  }

  // The single-child gender question has no options and keeps its translated labels; a
  // two-person gender field brings its own (Boy/Girl, or Mom/Dad for the parent).
  function buildChoiceField(field, itemId, param) {
    var hasOwnOptions = Array.isArray(field.options) && field.options.length > 0;
    var options = hasOwnOptions
      ? field.options.map(function (o) {
          return [o.value, o.label || o.value];
        })
      : [['boy', LABELS.boy], ['girl', LABELS.girl]];

    var wrap = document.createElement('div');
    wrap.className = 'tmt-md-gender';

    var group = document.createElement('div');
    group.className = 'options-selection personalisation-input';
    group.setAttribute('role', 'radiogroup');

    var labelId = 'tmt-md-' + param + '-label-' + itemId;
    group.setAttribute('aria-labelledby', labelId);

    var groupLabel = document.createElement('label');
    groupLabel.id = labelId;
    groupLabel.textContent = hasOwnOptions ? field.label || LABELS.gender : LABELS.gender;
    group.appendChild(groupLabel);

    var optionsEl = document.createElement('div');
    optionsEl.className = 'personalisation-options';
    group.appendChild(optionsEl);

    var buttons = [];

    options.forEach(function (option) {
      var value = option[0];
      var text = option[1];

      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'step-2-form-btn';
      btn.setAttribute('data-value', value);
      btn.setAttribute('role', 'radio');
      btn.setAttribute('aria-checked', 'false');
      btn.textContent = text;

      btn.addEventListener('click', function () {
        buttons.forEach(function (b) {
          var isSelected = b === btn;
          b.classList.toggle('selected', isSelected);
          b.setAttribute('aria-checked', isSelected ? 'true' : 'false');
        });
        clearCardError(itemId);
        setValue(itemId, param, value);
        updateSubmitEnabled();
      });

      buttons.push(btn);
      optionsEl.appendChild(btn);
    });

    wrap.appendChild(group);
    return wrap;
  }

  function setValue(itemId, key, value) {
    if (!state.values[itemId]) state.values[itemId] = {};
    state.values[itemId][key] = value;
  }

  function showCardError(itemId, message) {
    var el = $('tmt-md-card-error-' + itemId);
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
  }

  function clearCardError(itemId) {
    var el = $('tmt-md-card-error-' + itemId);
    if (!el) return;
    el.hidden = true;
  }

  function updateSubmitEnabled() {
    var btn = $('tmt-md-submit');
    if (!btn) return;
    btn.disabled = state.submitting || !M.canSubmit(state.items, state.values);
  }

  /* -------------------------------------------------------------- submit */

  function bindSubmit() {
    $('tmt-md-submit').addEventListener('click', function () {
      if (state.submitting || !M.canSubmit(state.items, state.values)) return;

      state.submitting = true;
      var btn = $('tmt-md-submit');
      btn.disabled = true;
      btn.textContent = 'Submitting…';

      var fields = M.buildSubmitPayloadFields(state.items, state.values);
      var formData = new FormData();
      fields.forEach(function (f) {
        formData.append(f.name, f.value);
      });

      fetch(state.apiBase + 'missing-details/' + encodeURIComponent(state.token) + '/submit', {
        method: 'POST',
        body: formData
      })
        .then(function (res) {
          if (res.status === 200) {
            showSuccess();
            return;
          }
          if (res.status === 207) {
            return res.json().then(handlePartialFailure);
          }
          throw new Error('HTTP ' + res.status);
        })
        .catch(function () {
          showStatus('Something went wrong submitting your details. Please try again, or reply to your order email and we’ll help.');
          $('tmt-md-items').hidden = false;
          $('tmt-md-actions').hidden = false;
        })
        .then(function () {
          state.submitting = false;
          btn.textContent = 'Submit';
          updateSubmitEnabled();
        });
    });
  }

  /**
   * A 207 means some items saved and some did not. The failed ones stay on screen (with
   * their own error) so the customer can fix and resubmit just those; items that succeeded
   * are dropped from `state.items` so a resubmit does not resend fields the server already
   * accepted.
   */
  function handlePartialFailure(data) {
    var results = (data && data.results) || [];
    var failedIds = {};

    results.forEach(function (r) {
      if (!r.ok) failedIds[r.id] = r.error || 'Something went wrong with this item. Please try again.';
    });

    results.forEach(function (r) {
      if (r.ok) {
        var card = $('tmt-md-card-' + r.id);
        if (card) card.remove();
      } else {
        showCardError(r.id, failedIds[r.id]);
      }
    });

    state.items = state.items.filter(function (item) {
      return Object.prototype.hasOwnProperty.call(failedIds, item.id);
    });

    if (state.items.length === 0) {
      showSuccess();
      return;
    }

    $('tmt-md-items').hidden = false;
    $('tmt-md-actions').hidden = false;
  }

  window.MissingDetailsPageUI = {
    init: init,
    state: state,
    load: load
  };
})();
